import { Helmet } from "react-helmet-async";
import { HOME_META } from "./seoMeta";

// Kept under Google's display limits — the previous default description was
// 198 characters and was truncated mid-sentence in search results.
const DEFAULT_TITLE = HOME_META.title;
const DEFAULT_DESCRIPTION = HOME_META.description;
const SITE_URL = "https://www.selectproposal.com";
const DEFAULT_IMAGE = `${SITE_URL}/og-image.png`;

/**
 * Reusable SEO head component — drop into any page.
 *
 * @param {string}  title        – page-specific title
 * @param {string}  description  – 150-160 char meta description
 * @param {string}  path         – pathname, e.g. "/pricing"
 * @param {string}  image        – full URL of OG image
 * @param {string}  type         – og:type, default "website"
 * @param {object}  jsonLd       – optional JSON-LD structured data object (single or array)
 * @param {boolean} noIndex      – true → noindex,nofollow
 * @param {string}  hreflangEn   – alternate English URL path
 * @param {string}  hreflangUr   – alternate Urdu URL path
 * @param {Array}   breadcrumbs  – array of {name, path} for BreadcrumbList schema
 */
const SEO = ({
  title = DEFAULT_TITLE,
  description = DEFAULT_DESCRIPTION,
  path = "",
  image = DEFAULT_IMAGE,
  type = "website",
  jsonLd = null,
  noIndex = false,
  hreflangEn = null,
  hreflangUr = null,
  breadcrumbs = null,
}) => {
  const canonical = `${SITE_URL}${path}`;

  // Build array of JSON-LD objects (supports multiple schemas per page)
  const jsonLdItems = [];
  if (jsonLd) {
    if (Array.isArray(jsonLd)) {
      jsonLdItems.push(...jsonLd);
    } else {
      jsonLdItems.push(jsonLd);
    }
  }
  if (breadcrumbs && breadcrumbs.length > 0) {
    jsonLdItems.push(breadcrumbSchema(breadcrumbs));
  }

  return (
    <Helmet>
      {/* Primary */}
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonical} />
      {noIndex && <meta name="robots" content="noindex,nofollow" />}

      {/* Hreflang */}
      {hreflangEn && (
        <link rel="alternate" hrefLang="en" href={`${SITE_URL}${hreflangEn}`} />
      )}
      {hreflangUr && (
        <link rel="alternate" hrefLang="ur" href={`${SITE_URL}${hreflangUr}`} />
      )}
      {(hreflangEn || hreflangUr) && (
        <link rel="alternate" hrefLang="x-default" href={canonical} />
      )}

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonical} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:site_name" content="Select Proposal" />
      <meta property="og:locale" content="en_PK" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {/* JSON-LD */}
      {jsonLdItems.map((item, idx) => (
        <script key={idx} type="application/ld+json">
          {JSON.stringify(item)}
        </script>
      ))}
    </Helmet>
  );
};

export default SEO;

/* ── Ready-made JSON-LD helpers ────────────────────────────── */

export const SITE_URL_EXPORT = SITE_URL;

export const organizationSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "@id": `${SITE_URL}/#organization`,
  name: "Select Proposal",
  url: SITE_URL,
  logo: `${SITE_URL}/new_logo.png`,
  description:
    "Pakistan's most trusted matrimonial service connecting families through verified profiles, shared values, and cultural compatibility.",
  foundingDate: "2025",
  contactPoint: {
    "@type": "ContactPoint",
    telephone: "+92-314-344-3051",
    contactType: "customer service",
    availableLanguage: ["English", "Urdu"],
  },
  sameAs: [
    "https://www.instagram.com/selectproposal",
    "https://www.facebook.com/selectproposal",
  ],
  founder: {
    "@type": "Person",
    "@id": `${SITE_URL}/#founder`,
    name: "Select Proposal Team",
  },
  knowsAbout: [
    "Pakistani matrimonial services",
    "Islamic marriage matchmaking",
    "Rishta services in Pakistan",
    "Muslim matrimony",
    "Halal matchmaking",
    "Pakistani wedding traditions",
    "Overseas Pakistani rishta",
    "Family-involved matchmaking",
    "Nikah guidance",
    "Online rishta platform",
  ],
  areaServed: [
    { "@type": "Country", name: "Pakistan" },
    { "@type": "Country", name: "United States" },
    { "@type": "Country", name: "United Kingdom" },
    { "@type": "Country", name: "United Arab Emirates" },
    { "@type": "Country", name: "Canada" },
    { "@type": "Country", name: "Saudi Arabia" },
    { "@type": "Country", name: "Australia" },
  ],
};

export const websiteSchema = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  "@id": `${SITE_URL}/#website`,
  name: "Select Proposal",
  url: SITE_URL,
  publisher: { "@id": `${SITE_URL}/#organization` },
  potentialAction: {
    "@type": "SearchAction",
    target: {
      "@type": "EntryPoint",
      urlTemplate: `${SITE_URL}/all-profiles?q={search_term_string}`,
    },
    "query-input": "required name=search_term_string",
  },
};

export const serviceSchema = {
  "@context": "https://schema.org",
  "@type": "Service",
  "@id": `${SITE_URL}/#service`,
  name: "Select Proposal Matrimonial Service",
  provider: { "@id": `${SITE_URL}/#organization` },
  serviceType: "Matrimonial & Matchmaking Service",
  description:
    "Online rishta and matrimonial service connecting Pakistani families worldwide. Browse verified profiles filtered by city, profession, and education.",
  areaServed: [
    { "@type": "Country", name: "Pakistan" },
    { "@type": "Country", name: "United States" },
    { "@type": "Country", name: "United Kingdom" },
    { "@type": "Country", name: "United Arab Emirates" },
    { "@type": "Country", name: "Canada" },
  ],
  hasOfferCatalog: {
    "@type": "OfferCatalog",
    name: "Membership Plans",
    itemListElement: [
      {
        "@type": "Offer",
        itemOffered: {
          "@type": "Service",
          name: "Free Plan",
          description: "Browse all profiles and get contacted by paid members",
        },
      },
      {
        "@type": "Offer",
        itemOffered: {
          "@type": "Service",
          name: "Select Plus Plan",
          description: "3 contact views per day for 3 months",
        },
      },
      {
        "@type": "Offer",
        itemOffered: {
          "@type": "Service",
          name: "Select Gold Plan",
          description: "6 contact views per day, compatibility scores and who viewed your profile, for 3 months",
        },
      },
      {
        "@type": "Offer",
        itemOffered: {
          "@type": "Service",
          name: "Select Elite Plan",
          description: "10 contact views per day, who viewed your profile and compatibility sorting, for 6 months",
        },
      },
    ],
  },
};

export const breadcrumbSchema = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((item, idx) => ({
    "@type": "ListItem",
    position: idx + 1,
    name: item.name,
    item: `${SITE_URL}${item.path}`,
  })),
});

export const faqSchema = (items) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: items.map((item) => ({
    "@type": "Question",
    name: item.question,
    acceptedAnswer: {
      "@type": "Answer",
      text: item.answer,
    },
  })),
});

export const blogPostSchema = (post) => ({
  "@context": "https://schema.org",
  "@type": "BlogPosting",
  headline: post.title,
  description: post.description || post.subtitle || "",
  image: post.image || DEFAULT_IMAGE,
  datePublished: post.publishedAt,
  dateModified: post.updatedAt || post.publishedAt,
  author: {
    "@type": "Organization",
    name: "Select Proposal",
  },
  publisher: {
    "@type": "Organization",
    name: "Select Proposal",
    logo: {
      "@type": "ImageObject",
      url: `${SITE_URL}/new_logo.png`,
    },
  },
  mainEntityOfPage: {
    "@type": "WebPage",
    "@id": `${SITE_URL}/blogs/${post.slug}`,
  },
});

export const howToSchema = {
  "@context": "https://schema.org",
  "@type": "HowTo",
  name: "How to Find a Rishta on Select Proposal",
  description:
    "Step-by-step guide to finding your perfect rishta match on Select Proposal — Pakistan's most trusted matrimonial service.",
  totalTime: "PT10M",
  step: [
    {
      "@type": "HowToStep",
      position: 1,
      name: "Create a Free Account",
      text: "Sign up on selectproposal.com with your basic details — name, age, city, and education. Registration is free and takes under 2 minutes.",
      url: `${SITE_URL}/signup`,
    },
    {
      "@type": "HowToStep",
      position: 2,
      name: "Complete Your Profile",
      text: "Add your photo, family details, profession, preferences, and what you're looking for in a partner. Complete profiles get 3x more interest.",
    },
    {
      "@type": "HowToStep",
      position: 3,
      name: "Browse Verified Profiles",
      text: "Use advanced filters to search by city, profession, education, marital status, and more. Every profile is manually verified by our team.",
      url: `${SITE_URL}/all-profiles`,
    },
    {
      "@type": "HowToStep",
      position: 4,
      name: "View Contact Details",
      text: "When you find a profile you like, use your daily contact views to see their phone number or WhatsApp. Free members can be contacted by paid members and can upgrade any time to start contacting.",
    },
    {
      "@type": "HowToStep",
      position: 5,
      name: "Connect with Families",
      text: "Reach out directly to the family. Our platform is designed for family-to-family connections — no chatting, no time-wasting.",
    },
  ],
};

export const cityLandingSchema = (city) => ({
  "@context": "https://schema.org",
  "@type": "Service",
  name: `Rishta Service in ${city.name}`,
  provider: { "@id": `${SITE_URL}/#organization` },
  serviceType: "Matrimonial & Matchmaking Service",
  areaServed: {
    "@type": "City",
    name: city.name,
    containedInPlace: {
      "@type": "Country",
      name: "Pakistan",
    },
  },
  description: `Find verified rishta proposals in ${city.name}. Browse ${city.name} matrimonial profiles filtered by education, profession & family values on Select Proposal.`,
  url: `${SITE_URL}/rishta-${city.slug}`,
});
